/**
 * One calendar for every place that has one (tasks, the dashboard block, database
 * calendar views): day, week or month, remembered per place. Items are placed on
 * their date (and time, in the day and week views); a place can let items be
 * dragged to another day or hour, and add an item on a day.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { addDays, fromIsoDate, toIsoDate } from '@nexus/core';
import { cn } from '../../lib/cn';
import { todayIso } from '../tasks/format';

export type CalendarMode = 'day' | 'week' | 'month';
const MODES: CalendarMode[] = ['day', 'week', 'month'];

export interface CalendarItem {
  id: string;
  date: string; // YYYY-MM-DD
  time: string | null; // HH:mm
}

const MAX_IN_MONTH = 4;
/** Hours shown in the day view (items outside them still show, at the edges). */
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const modeKey = (place: string) => `nexus.calendar.${place}`;
function loadMode(place: string): CalendarMode {
  try {
    const v = localStorage.getItem(modeKey(place));
    return MODES.includes(v as CalendarMode) ? (v as CalendarMode) : 'month';
  } catch {
    return 'month';
  }
}

const mondayOf = (date: string) => addDays(date, -((fromIsoDate(date).getDay() + 6) % 7));
const byTime = (a: CalendarItem, b: CalendarItem) =>
  a.time && b.time ? a.time.localeCompare(b.time) : a.time ? 1 : b.time ? -1 : 0;

export function Calendar<T extends CalendarItem>({
  place,
  items,
  renderItem,
  onDrop,
  onAdd,
  headingLevel = 2,
}: {
  /** Where this calendar is ("tasks", "dashboard", a view id): its mode is remembered per place. */
  place: string;
  items: T[];
  /** `compact` in the month grid; `mode` tells e.g. the day view (which shows the hour itself). */
  renderItem: (item: T, compact: boolean, mode: CalendarMode) => ReactNode;
  /** Dragged to another day (and hour, in the day view). Without it, items are not draggable. */
  onDrop?: (id: string, date: string, time: string | null) => void;
  onAdd?: (date: string, time: string | null) => void;
  headingLevel?: 2 | 3;
}) {
  const { t, i18n } = useTranslation();
  const today = todayIso();
  const [mode, setModeState] = useState<CalendarMode>(() => loadMode(place));
  const [anchor, setAnchor] = useState(today);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const setMode = (m: CalendarMode) => {
    setModeState(m);
    try {
      localStorage.setItem(modeKey(place), m);
    } catch {
      /* not remembered */
    }
  };

  const byDay = new Map<string, T[]>();
  for (const x of items) byDay.set(x.date, [...(byDay.get(x.date) ?? []), x]);
  for (const list of byDay.values()) list.sort(byTime);

  const fmt = (o: Intl.DateTimeFormatOptions, d: string) =>
    new Intl.DateTimeFormat(i18n.language, o).format(fromIsoDate(d));
  const anchorDate = fromIsoDate(anchor);
  const monthStart = toIsoDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1));
  const weekStart = mondayOf(anchor);
  const title =
    mode === 'month'
      ? fmt({ month: 'long', year: 'numeric' }, anchor)
      : mode === 'week'
        ? `${fmt({ day: 'numeric', month: 'short' }, weekStart)} – ${fmt(
            { day: 'numeric', month: 'short', year: 'numeric' },
            addDays(weekStart, 6),
          )}`
        : fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, anchor);
  const shift = (n: number) => {
    if (mode === 'day') setAnchor(addDays(anchor, n));
    else if (mode === 'week') setAnchor(addDays(anchor, 7 * n));
    else setAnchor(toIsoDate(new Date(anchorDate.getFullYear(), anchorDate.getMonth() + n, 1)));
  };
  const H = headingLevel === 2 ? 'h2' : 'h3';

  const dropZone = (key: string, date: string, time: string | null) =>
    onDrop
      ? {
          onDragOver: (e: React.DragEvent) => {
            if (!dragging) return;
            e.preventDefault();
            if (over !== key) setOver(key);
          },
          onDragLeave: () => setOver((o) => (o === key ? null : o)),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            if (dragging) onDrop(dragging, date, time);
            setDragging(null);
            setOver(null);
          },
        }
      : {};
  const item = (x: T, compact: boolean) => (
    <div
      key={x.id}
      draggable={!!onDrop}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', x.id);
        setDragging(x.id);
      }}
      onDragEnd={() => setDragging(null)}
      className={cn(dragging === x.id && 'opacity-50')}
    >
      {renderItem(x, compact, mode)}
    </div>
  );
  const dayNumber = (date: string, big = false) => (
    <span
      className={cn(
        'inline-grid place-items-center rounded-full tabular-nums',
        big ? 'size-7 text-sm' : 'size-5 text-xs',
        date === today ? 'bg-accent font-medium text-accent-fg' : 'text-fg-muted',
      )}
    >
      {fromIsoDate(date).getDate()}
    </span>
  );
  const addButton = (date: string, time: string | null) =>
    onAdd && (
      <button
        type="button"
        aria-label={t('calendar.addOn', { day: date })}
        onClick={() => onAdd(date, time)}
        className="rounded p-0.5 text-fg-muted opacity-0 group-hover/day:opacity-100 hover:bg-bg-hover focus:opacity-100 [@media(hover:none)]:opacity-100"
      >
        <Plus className="size-3" />
      </button>
    );

  return (
    <div data-testid="calendar" data-mode={mode}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => shift(-1)}
          aria-label={t(`calendar.prev.${mode}`)}
          className="rounded-full p-1.5 hover:bg-bg-hover"
        >
          <ChevronLeft className="size-4" />
        </button>
        <H className="min-w-40 text-center font-semibold first-letter:uppercase">{title}</H>
        <button
          type="button"
          onClick={() => shift(1)}
          aria-label={t(`calendar.next.${mode}`)}
          className="rounded-full p-1.5 hover:bg-bg-hover"
        >
          <ChevronRight className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setAnchor(today)}
          className="rounded-full px-3 py-1 text-sm hover:bg-bg-hover"
        >
          {t('tasks.today')}
        </button>
        <div
          role="radiogroup"
          aria-label={t('calendar.mode')}
          className="ml-auto flex rounded-full bg-bg-subtle p-0.5"
        >
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => setMode(m)}
              className={cn(
                'rounded-full px-3 py-1 text-sm',
                mode === m ? 'bg-bg font-medium shadow-sheet' : 'text-fg-muted hover:text-fg',
              )}
            >
              {t(`calendar.modes.${m}`)}
            </button>
          ))}
        </div>
      </div>

      {mode === 'month' && (
        <div
          className="grid grid-cols-7 overflow-hidden rounded-xl border border-border"
          role="grid"
          aria-label={title}
        >
          {Array.from({ length: 7 }, (_, i) => addDays(mondayOf(monthStart), i)).map((d) => (
            <div
              key={`h${d}`}
              role="columnheader"
              className="border-b border-border bg-bg-subtle px-2 py-1 text-xs text-fg-muted capitalize"
            >
              {fmt({ weekday: 'short' }, d)}
            </div>
          ))}
          {Array.from({ length: 42 }, (_, i) => addDays(mondayOf(monthStart), i)).map((day) => {
            const list = byDay.get(day) ?? [];
            const inMonth = day.slice(0, 7) === monthStart.slice(0, 7);
            return (
              <div
                key={day}
                role="gridcell"
                aria-label={day}
                {...dropZone(day, day, null)}
                className={cn(
                  'group/day min-h-24 min-w-0 border-r border-b border-border p-1 [&:nth-child(7n)]:border-r-0',
                  !inMonth && 'bg-bg-subtle/60',
                  over === day && 'ring-2 ring-accent ring-inset',
                )}
              >
                <div className="mb-1 flex items-center justify-between">
                  <button
                    type="button"
                    aria-label={t('calendar.openDay', { day })}
                    onClick={() => {
                      setAnchor(day);
                      setMode('day');
                    }}
                    className="rounded-full hover:bg-bg-hover"
                  >
                    {dayNumber(day)}
                  </button>
                  {addButton(day, null)}
                </div>
                {list.slice(0, MAX_IN_MONTH).map((x) => item(x, true))}
                {list.length > MAX_IN_MONTH && (
                  <button
                    type="button"
                    onClick={() => {
                      setAnchor(day);
                      setMode('day');
                    }}
                    className="text-xs text-fg-muted hover:text-fg"
                  >
                    {t('tasks.more', { count: list.length - MAX_IN_MONTH })}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mode === 'week' && (
        <div
          className="grid grid-cols-1 overflow-hidden rounded-xl border border-border sm:grid-cols-7"
          role="grid"
          aria-label={title}
        >
          {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day) => (
            <div
              key={day}
              role="gridcell"
              aria-label={day}
              {...dropZone(day, day, null)}
              className={cn(
                'group/day min-w-0 border-b border-border p-2 sm:min-h-72 sm:border-r sm:border-b-0 sm:[&:nth-child(7n)]:border-r-0',
                over === day && 'ring-2 ring-accent ring-inset',
              )}
            >
              <div className="mb-2 flex items-center gap-1.5">
                <span className="text-xs text-fg-muted capitalize">
                  {fmt({ weekday: 'short' }, day)}
                </span>
                {dayNumber(day, true)}
                <span className="flex-1" />
                {addButton(day, null)}
              </div>
              <div className="flex flex-col gap-1">
                {(byDay.get(day) ?? []).map((x) => item(x, false))}
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'day' && (
        <div
          className="overflow-hidden rounded-xl border border-border"
          role="grid"
          aria-label={title}
        >
          {(() => {
            const list = byDay.get(anchor) ?? [];
            const allDay = list.filter((x) => !x.time);
            return (
              <>
                <div
                  role="row"
                  {...dropZone(`${anchor}|all`, anchor, null)}
                  className={cn(
                    'group/day flex gap-3 border-b border-border bg-bg-subtle px-3 py-2',
                    over === `${anchor}|all` && 'ring-2 ring-accent ring-inset',
                  )}
                >
                  <span className="w-12 shrink-0 pt-0.5 text-xs text-fg-muted">
                    {t('calendar.allDay')}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {allDay.map((x) => item(x, false))}
                  </div>
                  {addButton(anchor, null)}
                </div>
                {HOURS.map((h) => {
                  const hour = `${String(h).padStart(2, '0')}:00`;
                  const inHour = list.filter((x) => x.time && Number(x.time.slice(0, 2)) === h);
                  // Night hours only when something is planned then.
                  if ((h < 7 || h > 21) && !inHour.length) return null;
                  const key = `${anchor}|${hour}`;
                  return (
                    <div
                      key={hour}
                      role="row"
                      {...dropZone(key, anchor, hour)}
                      className={cn(
                        'group/day flex min-h-11 gap-3 border-b border-border px-3 py-1.5 last:border-b-0',
                        over === key && 'ring-2 ring-accent ring-inset',
                      )}
                    >
                      <span className="w-12 shrink-0 pt-0.5 text-xs text-fg-muted tabular-nums">
                        {hour}
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {inHour.map((x) => item(x, false))}
                      </div>
                      {addButton(anchor, hour)}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
